import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { Modal } from "./Modal";

function newSectionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return `app-${crypto.randomUUID()}`;
  return `app-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function normalizeSections(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((s) => s && typeof s === "object" && (s.kind === "paragraph" || s.kind === "table"))
    .map((s) => {
      if (s.kind === "paragraph") {
        return { id: s.id || newSectionId(), kind: "paragraph", title: String(s.title || ""), body: String(s.body || "") };
      }
      const columns = Array.isArray(s.columns) ? s.columns.map((c) => String(c || "")) : [];
      let rows = [];
      if (Array.isArray(s.rows)) {
        rows = s.rows.map((r) => {
          if (r && typeof r === "object" && "label" in r) {
            const cells = Array.isArray(r.cells) ? r.cells.map((c) => String(c ?? "")) : [];
            return { label: String(r.label || ""), cells: columns.map((_, i) => cells[i] ?? "") };
          }
          if (Array.isArray(r)) {
            return { label: String(r[0] ?? ""), cells: columns.map((_, i) => String(r[i + 1] ?? "")) };
          }
          return { label: "", cells: columns.map(() => "") };
        });
      }
      return { id: s.id || newSectionId(), kind: "table", title: String(s.title || ""), columns, rows };
    });
}

export { normalizeSections };

/**
 * User-added sections after the fixed MSA template (paragraph or table). Not shown on wizard tabs.
 */
export function ContractAppendSections({
  sections = [],
  onChange,
  editable = false,
  annotateAnchors = false,
  /** Displayed section numbers: first block uses this value (01 with padStart). */
  numberOffset = 1,
}) {
  const [draftTitle, setDraftTitle] = useState("");
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [colInput, setColInput] = useState("");
  const [rowInput, setRowInput] = useState("");

  const ax = (anchorId) => (annotateAnchors && anchorId ? { "data-contract-anchor": anchorId } : {});

  const setSections = (next) => {
    if (typeof onChange === "function") onChange(normalizeSections(next));
  };

  const updateSection = (id, patch) => {
    setSections(sections.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const removeSection = (id) => {
    setSections(sections.filter((s) => s.id !== id));
  };

  const addParagraph = () => {
    const t = draftTitle.trim();
    if (!t) return;
    setSections([...sections, { id: newSectionId(), kind: "paragraph", title: t, body: "" }]);
    setDraftTitle("");
  };

  const openTableModal = () => {
    if (!draftTitle.trim()) return;
    setColInput("");
    setRowInput("");
    setTableModalOpen(true);
  };

  const confirmTable = () => {
    const cols = colInput
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const rowLabels = rowInput
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!cols.length || !rowLabels.length) return;
    const rows = rowLabels.map((label) => ({
      label,
      cells: cols.map(() => ""),
    }));
    setSections([
      ...sections,
      {
        id: newSectionId(),
        kind: "table",
        title: draftTitle.trim(),
        columns: cols,
        rows,
      },
    ]);
    setDraftTitle("");
    setTableModalOpen(false);
  };

  const badgeColor = (index) => (index % 2 === 0 ? "bg-[color:var(--color-accent-fill)]" : "bg-[color:var(--color-text-muted-dark)]");

  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      sections.map((s, idx) => {
        const num = String(numberOffset + idx).padStart(2, "0");
        const anchor = `append.${s.id}`;
        return /* @__PURE__ */ jsxs(
          "div",
          {
            className: "rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden mb-6",
            ...ax(anchor),
            children: [
              /* @__PURE__ */ jsxs("div", {
                className: "flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100 bg-white",
                children: [
                  /* @__PURE__ */ jsxs("div", {
                    className: "flex items-center gap-3 min-w-0",
                    children: [
                      /* @__PURE__ */ jsx("span", {
                        className: `shrink-0 w-9 h-9 rounded-full ${badgeColor(idx)} text-white text-sm font-black flex items-center justify-center`,
                        children: num,
                      }),
                      /* @__PURE__ */ jsx("h3", {
                        className: "text-sm font-black uppercase tracking-wide text-slate-900 truncate",
                        children: s.title || "Untitled section",
                      }),
                    ],
                  }),
                  editable &&
                    /* @__PURE__ */ jsx("button", {
                      type: "button",
                      onClick: () => removeSection(s.id),
                      className: "shrink-0 p-2 rounded-lg text-rose-500 hover:bg-rose-50 transition-colors",
                      title: "Remove section",
                      children: /* @__PURE__ */ jsx("svg", {
                        className: "w-4 h-4",
                        fill: "none",
                        stroke: "currentColor",
                        viewBox: "0 0 24 24",
                        children: /* @__PURE__ */ jsx("path", {
                          strokeLinecap: "round",
                          strokeLinejoin: "round",
                          strokeWidth: "2",
                          d: "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16",
                        }),
                      }),
                    }),
                ],
              }),
              /* @__PURE__ */ jsx("div", {
                className: "px-5 py-4",
                children:
                  s.kind === "paragraph"
                    ? editable
                      ? /* @__PURE__ */ jsx("textarea", {
                          value: s.body || "",
                          onChange: (e) => updateSection(s.id, { body: e.target.value }),
                          rows: 6,
                          className:
                            "w-full text-sm text-slate-700 border border-slate-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500/20 min-h-[120px]",
                          placeholder: "Enter section text\u2026",
                        })
                      : /* @__PURE__ */ jsx("p", {
                          className: "text-sm text-slate-700 whitespace-pre-wrap leading-relaxed",
                          children: s.body || "-",
                        })
                    : /* @__PURE__ */ jsx("div", {
                        className: "overflow-x-auto rounded-xl border border-slate-200",
                        children: /* @__PURE__ */ jsxs("table", {
                          className: "w-full text-left text-xs",
                          children: [
                            /* @__PURE__ */ jsx("thead", {
                              children: /* @__PURE__ */ jsxs("tr", {
                                className: "bg-slate-100 text-[9px] font-black uppercase tracking-widest text-blue-700",
                                children: [
                                  /* @__PURE__ */ jsx("th", {
                                    className: "px-3 py-2.5 w-36 border-r border-slate-200",
                                    children: "\u00A0",
                                  }),
                                  ...s.columns.map((h, hi) =>
                                    /* @__PURE__ */ jsx("th", { className: "px-3 py-2.5", children: h }, hi)
                                  ),
                                ],
                              }),
                            }),
                            /* @__PURE__ */ jsx("tbody", {
                              className: "divide-y divide-slate-100",
                              children: (s.rows || []).map((row, ri) =>
                                /* @__PURE__ */ jsxs(
                                  "tr",
                                  {
                                    className: ri % 2 === 0 ? "bg-white" : "bg-slate-50/80",
                                    children: [
                                      /* @__PURE__ */ jsx("td", {
                                        className: "px-3 py-2 font-semibold text-slate-600 border-r border-slate-100",
                                        children: row.label || `-`,
                                      }),
                                      ...(row.cells || []).map((cell, ci) =>
                                        /* @__PURE__ */ jsx(
                                          "td",
                                          {
                                            className: "px-3 py-2 text-slate-600",
                                            children: editable
                                              ? /* @__PURE__ */ jsx("input", {
                                                  value: cell ?? "",
                                                  onChange: (e) => {
                                                    const nextRows = [...s.rows];
                                                    const nr = { ...nextRows[ri], cells: [...(nextRows[ri].cells || [])] };
                                                    nr.cells[ci] = e.target.value;
                                                    nextRows[ri] = nr;
                                                    updateSection(s.id, { rows: nextRows });
                                                  },
                                                  className:
                                                    "w-full min-w-[64px] bg-blue-50/50 border border-blue-100 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-blue-300",
                                                })
                                              : cell || "-",
                                          },
                                          ci
                                        )
                                      ),
                                    ],
                                  },
                                  ri
                                )
                              ),
                            }),
                          ],
                        }),
                      }),
              }),
            ],
          },
          s.id
        );
      }),
      editable &&
        /* @__PURE__ */ jsxs("div", {
          className:
            "rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50/30 px-5 py-4 mb-6",
          children: [
            /* @__PURE__ */ jsx("p", {
              className: "text-[10px] font-black uppercase tracking-widest text-blue-600 mb-3",
              children: "Add new section",
            }),
            /* @__PURE__ */ jsxs("div", {
              className: "flex flex-col sm:flex-row sm:items-center gap-3",
              children: [
                /* @__PURE__ */ jsx("input", {
                  type: "text",
                  value: draftTitle,
                  onChange: (e) => setDraftTitle(e.target.value),
                  placeholder: "Section title\u2026",
                  className:
                    "flex-1 min-w-0 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/25",
                }),
                /* @__PURE__ */ jsxs("div", {
                  className: "flex flex-wrap gap-2 shrink-0",
                  children: [
                    /* @__PURE__ */ jsxs("button", {
                      type: "button",
                      onClick: addParagraph,
                      className:
                        "inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-[10px] font-black uppercase tracking-widest text-slate-800 hover:bg-blue-50 hover:border-blue-200 transition-colors",
                      children: [
                        /* @__PURE__ */ jsx("span", { className: "text-slate-500 font-serif font-bold", children: "T" }),
                        "Paragraph",
                      ],
                    }),
                    /* @__PURE__ */ jsxs("button", {
                      type: "button",
                      onClick: openTableModal,
                      className:
                        "inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-[10px] font-black uppercase tracking-widest text-slate-800 hover:bg-blue-50 hover:border-blue-200 transition-colors",
                      children: [
                        /* @__PURE__ */ jsx("svg", {
                          className: "w-3.5 h-3.5 text-slate-500",
                          fill: "none",
                          stroke: "currentColor",
                          viewBox: "0 0 24 24",
                          children: /* @__PURE__ */ jsx("path", {
                            strokeLinecap: "round",
                            strokeLinejoin: "round",
                            strokeWidth: "2",
                            d: "M3 10h18M3 14h18M10 3v18M14 3v18",
                          }),
                        }),
                        "Table",
                      ],
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
      tableModalOpen &&
        /* @__PURE__ */ jsx(Modal, {
          isOpen: tableModalOpen,
          onClose: () => setTableModalOpen(false),
          title: "New table section",
          children: /* @__PURE__ */ jsxs("div", {
            className: "space-y-4 p-2",
            children: [
              /* @__PURE__ */ jsxs("p", {
                className: "text-xs text-slate-600",
                children: [
                  "Section title: ",
                  /* @__PURE__ */ jsx("strong", { children: draftTitle.trim() || "(none)" }),
                ],
              }),
              /* @__PURE__ */ jsxs("div", {
                children: [
                  /* @__PURE__ */ jsx("label", {
                    className: "block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1",
                    children: "Column names (one per line)",
                  }),
                  /* @__PURE__ */ jsx("textarea", {
                    value: colInput,
                    onChange: (e) => setColInput(e.target.value),
                    rows: 4,
                    className:
                      "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20",
                    placeholder: "Column 1\nColumn 2",
                  }),
                ],
              }),
              /* @__PURE__ */ jsxs("div", {
                children: [
                  /* @__PURE__ */ jsx("label", {
                    className: "block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1",
                    children: "Row names (one per line)",
                  }),
                  /* @__PURE__ */ jsx("textarea", {
                    value: rowInput,
                    onChange: (e) => setRowInput(e.target.value),
                    rows: 4,
                    className:
                      "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20",
                    placeholder: "Row A\nRow B",
                  }),
                ],
              }),
              /* @__PURE__ */ jsxs("div", {
                className: "flex justify-end gap-2 pt-2",
                children: [
                  /* @__PURE__ */ jsx("button", {
                    type: "button",
                    onClick: () => setTableModalOpen(false),
                    className: "px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100",
                    children: "Cancel",
                  }),
                  /* @__PURE__ */ jsx("button", {
                    type: "button",
                    onClick: confirmTable,
                    className:
                      "px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest bg-[color:var(--color-accent-fill)] text-white hover:opacity-90",
                    children: "Create table",
                  }),
                ],
              }),
            ],
          }),
        }),
    ],
  });
}
